const FIT_RESOURCE_LIST_SECTION_SELECTOR = '[data-resource-list-order-enabled]';
const FIT_RESOURCE_LIST_BLOCK_SOURCE_SELECTOR = '.section-resource-list__blocks-source';
const FIT_RESOURCE_LIST_BLOCKS_ORDERED_SELECTOR = '.section-resource-list__blocks-ordered';
const FIT_RESOURCE_LIST_WRAPPER_SELECTOR = '[data-resource-list-wrapper]';
const FIT_COLLECTION_ITEM_CARD_SELECTOR = '[data-fit-collection-item-settings]';
const FIT_COLLECTION_ITEM_TITLE_STYLE_CLASSES = ['rte', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'custom'];
const FIT_COLLECTION_ITEM_MODIFIER_CLASSES = [
  'fc-ci--overlaid',
  'fc-ci--v-center',
  'fc-ci--v-start',
  'fc-ci--v-end',
  'fc-ci--title-underline-hover',
];
const FIT_COLLECTION_ITEM_TYPOGRAPHY_PROPERTIES = ['font-family', 'font-size', 'line-height', 'letter-spacing', 'color'];
const FIT_COLLECTION_ITEM_SURFACE_PROPERTIES = [
  '--color-background',
  '--color-background-rgb',
  '--color',
  '--color-rgb',
  '--color-foreground',
  '--color-foreground-rgb',
  '--color-foreground-muted',
  '--color-foreground-subdued',
  '--color-border',
  '--color-border-rgb',
  '--fit-opacity-subtle',
  '--fit-opacity-light',
  '--fit-opacity-medium',
  '--fit-opacity-strong',
  '--fit-opacity-accent',
];
const fitResourceListObservers = new WeakMap();

function fitIsShopifyDesignMode() {
  return document.documentElement.classList.contains('shopify-design-mode');
}

function fitGetSectionRoots(fitRoot = document) {
  if (!(fitRoot instanceof Document || fitRoot instanceof Element)) {
    return [];
  }

  if (fitRoot instanceof Element && fitRoot.matches(FIT_RESOURCE_LIST_SECTION_SELECTOR)) {
    return [fitRoot];
  }

  if (fitRoot instanceof Element) {
    const fitClosestSection = fitRoot.closest(FIT_RESOURCE_LIST_SECTION_SELECTOR);
    if (fitClosestSection) {
      return [fitClosestSection];
    }
  }

  return Array.from(fitRoot.querySelectorAll(FIT_RESOURCE_LIST_SECTION_SELECTOR));
}

function fitGetDeclaredBlockType(fitBlock) {
  return (
    fitBlock.getAttribute('data-block-type') ||
    fitBlock.querySelector('[data-block-type]')?.getAttribute('data-block-type') ||
    ''
  );
}

function fitIsTargetBlock(fitBlock, fitTargetBlockType) {
  if (!(fitBlock instanceof Element) || typeof fitTargetBlockType !== 'string' || !fitTargetBlockType) {
    return false;
  }

  const fitDeclaredBlockType = fitGetDeclaredBlockType(fitBlock);
  if (fitDeclaredBlockType) {
    return fitDeclaredBlockType === fitTargetBlockType;
  }

  const fitBlockId = fitBlock.id || '';
  const fitBlockClassName = typeof fitBlock.className === 'string' ? fitBlock.className : '';
  return fitBlockId.includes(fitTargetBlockType) || fitBlockClassName.includes(fitTargetBlockType);
}

function fitRevealWrapper(fitWrapper, fitBlocksOrdered) {
  if (!(fitWrapper instanceof HTMLElement) || !(fitBlocksOrdered instanceof HTMLElement)) {
    return;
  }

  fitWrapper.style.display = '';
  fitBlocksOrdered.appendChild(fitWrapper);
}

function fitCopyCollectionItemSurface(fitSource, fitTarget) {
  if (!(fitSource instanceof HTMLElement) || !(fitTarget instanceof HTMLElement)) return;

  const fitSourceStyle = getComputedStyle(fitSource);
  FIT_COLLECTION_ITEM_SURFACE_PROPERTIES.forEach((fitProperty) => {
    fitTarget.style.setProperty(fitProperty, fitSourceStyle.getPropertyValue(fitProperty));
  });
  fitTarget.style.backgroundColor = fitSourceStyle.backgroundColor;
  fitTarget.style.color = fitSourceStyle.color;
}

function fitSyncCollectionItemCard(fitTemplateCard, fitTargetCard) {
  if (!(fitTemplateCard instanceof HTMLElement) || !(fitTargetCard instanceof HTMLElement)) return;
  if (fitTemplateCard === fitTargetCard) return;

  ['placement', 'showImage', 'showTitle', 'titleTextStyle'].forEach((fitKey) => {
    fitTargetCard.dataset[fitKey] = fitTemplateCard.dataset[fitKey] || '';
  });

  FIT_COLLECTION_ITEM_MODIFIER_CLASSES.forEach((fitClassName) => {
    fitTargetCard.classList.toggle(fitClassName, fitTemplateCard.classList.contains(fitClassName));
  });

  fitTargetCard.style.cssText = fitTemplateCard.style.cssText;

  const fitTemplateWrapper = fitTemplateCard.closest('.fc-ci-wrap');
  const fitTargetWrapper = fitTargetCard.closest('.fc-ci-wrap');
  if (fitTemplateWrapper instanceof HTMLElement && fitTargetWrapper instanceof HTMLElement) {
    fitTargetWrapper.style.cssText = fitTemplateWrapper.style.cssText;
    fitCopyCollectionItemSurface(fitTemplateWrapper, fitTargetWrapper);
  }
  fitCopyCollectionItemSurface(fitTemplateCard, fitTargetCard);

  const fitTemplateTitle = fitTemplateCard.querySelector('.fc-ci__title');
  const fitTargetTitle = fitTargetCard.querySelector('.fc-ci__title');
  if (fitTemplateTitle instanceof HTMLElement && fitTargetTitle instanceof HTMLElement) {
    fitTargetTitle.style.cssText = fitTemplateTitle.style.cssText;
    FIT_COLLECTION_ITEM_TITLE_STYLE_CLASSES.forEach((fitClassName) => {
      fitTargetTitle.classList.toggle(fitClassName, fitTemplateTitle.classList.contains(fitClassName));
    });

    const fitTemplateTypographyNodes = [fitTemplateTitle, ...fitTemplateTitle.querySelectorAll('*')];
    const fitTargetTypographyNodes = [fitTargetTitle, ...fitTargetTitle.querySelectorAll('*')];
    fitTargetTypographyNodes.forEach((fitTargetNode, fitIndex) => {
      const fitTemplateNode = fitTemplateTypographyNodes[fitIndex];
      if (!(fitTemplateNode instanceof HTMLElement) || !(fitTargetNode instanceof HTMLElement)) return;

      const fitTemplateNodeStyle = getComputedStyle(fitTemplateNode);
      FIT_COLLECTION_ITEM_TYPOGRAPHY_PROPERTIES.forEach((fitProperty) => {
        fitTargetNode.style.setProperty(fitProperty, fitTemplateNodeStyle.getPropertyValue(fitProperty));
      });
    });
  }
}

function fitCollectionItemStateSignature(fitTemplateCard) {
  if (!(fitTemplateCard instanceof HTMLElement)) return '';

  const fitTemplateWrapper = fitTemplateCard.closest('.fc-ci-wrap');
  const fitTemplateTitle = fitTemplateCard.querySelector('.fc-ci__title');

  return JSON.stringify({
    cardClass: fitTemplateCard.className,
    cardStyle: fitTemplateCard.style.cssText,
    placement: fitTemplateCard.dataset.placement || '',
    showImage: fitTemplateCard.dataset.showImage || '',
    showTitle: fitTemplateCard.dataset.showTitle || '',
    titleTextStyle: fitTemplateCard.dataset.titleTextStyle || '',
    wrapperClass: fitTemplateWrapper?.className || '',
    wrapperStyle: fitTemplateWrapper?.style.cssText || '',
    titleClass: fitTemplateTitle?.className || '',
    titleStyle: fitTemplateTitle?.style.cssText || '',
  });
}

function fitSyncCollectionItemSettings(fitSection, { force = false } = {}) {
  if (!fitIsShopifyDesignMode()) return;
  if (!(fitSection instanceof HTMLElement) || fitSection.dataset.resourceListBlockType !== '_collection-item') return;

  const fitCards = Array.from(fitSection.querySelectorAll(FIT_COLLECTION_ITEM_CARD_SELECTOR));
  if (fitCards.length < 2) return;

  const fitTemplateCard = fitCards.find((fitCard) => !/-\d+$/.test(fitCard.dataset.blockId || '')) || fitCards[0];
  const fitObserverState = fitResourceListObservers.get(fitSection);
  const fitStateSignature = fitCollectionItemStateSignature(fitTemplateCard);

  if (!force && fitObserverState?.lastStateSignature === fitStateSignature) return;

  fitCards.forEach((fitCard) => fitSyncCollectionItemCard(fitTemplateCard, fitCard));

  if (fitObserverState) {
    fitObserverState.lastStateSignature = fitStateSignature;
  }
}

function fitDisconnectCollectionItemSettings(fitSection) {
  if (!(fitSection instanceof HTMLElement)) return;

  const fitObserverState = fitResourceListObservers.get(fitSection);
  if (!fitObserverState) return;

  fitObserverState.observer.disconnect();
  if (fitObserverState.frameId) {
    cancelAnimationFrame(fitObserverState.frameId);
  }

  fitResourceListObservers.delete(fitSection);
}

function fitScheduleCollectionItemSettingsSync(fitSection) {
  const fitObserverState = fitResourceListObservers.get(fitSection);
  if (!fitObserverState || fitObserverState.frameId) return;

  fitObserverState.frameId = requestAnimationFrame(() => {
    fitObserverState.frameId = 0;

    if (!fitSection.isConnected) {
      fitDisconnectCollectionItemSettings(fitSection);
      return;
    }

    fitSyncCollectionItemSettings(fitSection);
  });
}

function fitObserveCollectionItemSettings(fitSection) {
  if (!fitIsShopifyDesignMode()) return;
  if (!(fitSection instanceof HTMLElement) || fitSection.dataset.resourceListBlockType !== '_collection-item') return;
  if (fitResourceListObservers.has(fitSection)) return;

  const fitObserver = new MutationObserver(() => fitScheduleCollectionItemSettingsSync(fitSection));
  fitObserver.observe(fitSection, { childList: true, subtree: true });
  fitResourceListObservers.set(fitSection, {
    observer: fitObserver,
    frameId: 0,
    lastStateSignature: '',
  });
}

function fitOrganizeSection(fitSection) {
  if (!(fitSection instanceof HTMLElement)) return;

  const fitTargetBlockType = fitSection.dataset.resourceListBlockType || '';
  const fitBlocksSource = fitSection.querySelector(FIT_RESOURCE_LIST_BLOCK_SOURCE_SELECTOR);
  const fitBlocksOrdered = fitSection.querySelector(FIT_RESOURCE_LIST_BLOCKS_ORDERED_SELECTOR);
  const fitWrapper = fitSection.querySelector(FIT_RESOURCE_LIST_WRAPPER_SELECTOR);

  if (!(fitBlocksSource instanceof HTMLElement) || !(fitBlocksOrdered instanceof HTMLElement) || !fitTargetBlockType) {
    fitObserveCollectionItemSettings(fitSection);
    fitSyncCollectionItemSettings(fitSection, { force: true });
    return;
  }

  const fitAllBlocks = Array.from(fitBlocksSource.children);
  const fitFirstTargetIndex = fitAllBlocks.findIndex((fitBlock) => fitIsTargetBlock(fitBlock, fitTargetBlockType));

  if (fitFirstTargetIndex === -1) {
    fitAllBlocks.forEach((fitBlock) => fitBlocksOrdered.appendChild(fitBlock));
    fitRevealWrapper(fitWrapper, fitBlocksOrdered);
    fitBlocksSource.remove();
    fitObserveCollectionItemSettings(fitSection);
    fitSyncCollectionItemSettings(fitSection, { force: true });
    return;
  }

  fitAllBlocks.forEach((fitBlock, fitIndex) => {
    const fitMatchesTarget = fitIsTargetBlock(fitBlock, fitTargetBlockType);

    if (fitMatchesTarget && fitIndex === fitFirstTargetIndex) {
      fitRevealWrapper(fitWrapper, fitBlocksOrdered);
    }

    if (fitMatchesTarget) {
      fitBlock.style.display = 'none';
    }

    fitBlocksOrdered.appendChild(fitBlock);
  });

  fitBlocksSource.remove();
  fitObserveCollectionItemSettings(fitSection);
  fitSyncCollectionItemSettings(fitSection, { force: true });
}

function fitOrganizeResourceListSections(fitRoot = document) {
  fitGetSectionRoots(fitRoot).forEach((fitSection) => fitOrganizeSection(fitSection));
}

function fitHandleResourceListEvent(fitEvent) {
  const fitEventTarget = fitEvent?.target;
  if (fitEventTarget instanceof Element || fitEventTarget instanceof Document) {
    fitOrganizeResourceListSections(fitEventTarget);
    return;
  }

  fitOrganizeResourceListSections(document);
}

function fitHandleResourceListUnload(fitEvent) {
  fitGetSectionRoots(fitEvent?.target).forEach((fitSection) => fitDisconnectCollectionItemSettings(fitSection));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => fitOrganizeResourceListSections(document), { once: true });
} else {
  fitOrganizeResourceListSections(document);
}

document.addEventListener('shopify:section:load', fitHandleResourceListEvent);
document.addEventListener('shopify:section:unload', fitHandleResourceListUnload);
document.addEventListener('shopify:block:select', fitHandleResourceListEvent);

window.FitCheck = window.FitCheck || {};
window.FitCheck.resourceListOrder = {
  organize: fitOrganizeResourceListSections,
};
